import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { AUTH_PATHS } from "../../../constants";
import {
  isRecoveryToken,
  isWelcomeToken,
  parseAuthLinkToken,
} from "../../../hooks/auth/authLinkToken";
import { getPostAuthRedirect } from "../../../utils";
import { Loading } from "../../ui";
import { AuthLinkConfirm } from "../AuthLinkConfirm";
import { AuthUpdate } from "../AuthUpdate";

export const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useMemo(() => parseAuthLinkToken(location), [location.search, location.hash]);

  const handleConfirmed = useCallback(() => {
    const { redirectPath } = getPostAuthRedirect({ location });
    navigate(redirectPath, { replace: true });
  }, [location, navigate]);

  const goToSignIn = useCallback(() => {
    navigate(AUTH_PATHS.SIGNIN, { replace: true });
  }, [navigate]);

  // A recovery link landing on /auth/callback (rather than its usual /auth/update
  // destination) still needs AuthUpdate's password-reset gate and form, not the
  // sign-in gate below.
  if (token && isRecoveryToken(token)) {
    return <AuthUpdate />;
  }

  // The welcome card is the confirm gate itself: one click consumes the invite/
  // email-confirmation token and hands off to sign-in, rather than showing a second card
  // that repeats the same call to action. An invited user carries password_reset_required,
  // so the machine re-routes them on to the set-password form from there.
  if (token && isWelcomeToken(token)) {
    return (
      <AuthLinkConfirm variant="welcome" onConfirmed={goToSignIn} onNoLink={goToSignIn}>
        <Loading />
      </AuthLinkConfirm>
    );
  }

  return (
    <AuthLinkConfirm variant="welcome" onConfirmed={handleConfirmed} onNoLink={goToSignIn}>
      <Loading />
    </AuthLinkConfirm>
  );
};
